import type { AppProps } from 'next/app'
import Router, { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import NProgress from 'nprogress'
import AOS from 'aos'

import { Red_Hat_Display } from 'next/font/google'

import 'aos/dist/aos.css'
import 'lazysizes'

import '@/styles/main.scss'
import { AuthProvider } from 'react-oidc-context'
import { IAuthUrl } from '@/utils/types'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Cookie } from '@/utils/functions/cookie'
import ReactSnackbarProvider from '@/utils/functions/ReactSnackbarProvider'
import { registerServiceWorker } from '@/utils/functions/registerServiceWorker'
import { GTMEvent } from '@/utils/gtm'
import { Akiva } from '@/utils/akiva/akiva'
import { AkivaType } from '@/utils/akiva/types'
import { AppContextProps } from '@wap-client/core'
import useLoginChecker from '@/hooks/useLoginChecker'

export const redHat = Red_Hat_Display({
  subsets: ['latin'],
  display: 'swap',
})

function MyApp({ Component, pageProps }: AppProps) {
  const [lang, setLang] = useState<string>(() => {
    return Cookie.get('language') || 'en'
  })
  const router = useRouter()
  const { loginChecker } = useLoginChecker()

  const httpUrl = `${pageProps?.headers?.host?.includes('localhost') ? 'http' : 'https'}://`

  const oidcConfig = {
    authority: process.env.NEXT_PUBLIC_IDENTITY_SERVER_AUTHORITY || '',
    client_id: process.env.NEXT_PUBLIC_IDENTITY_SERVER_CLIENT_ID || '',
    redirect_uri: `${httpUrl}${pageProps?.headers?.host}/auth/${IAuthUrl.REDIRECT}`,
    scope: process.env.NEXT_PUBLIC_IDENTITY_SERVER_SCOPE,
  }

  const handleRouteChange = (nextUrl?: string, previousUrl?: string | null) => {
    window.dataLayer = window.dataLayer || []

    const currentUrl = previousUrl || window.location.href
    const currentHost = new URL(currentUrl).hostname
    const currentSubDomain =
      currentHost.split('.').length > 2 ? currentHost.split('.')[0] : null

    let nextPage = null
    let nextSubDomain = null

    if (nextUrl) {
      const nextFullUrl = nextUrl.startsWith('http')
        ? nextUrl
        : `${window.location.protocol}//${window.location.host}${nextUrl}`

      const nextUrlObj = new URL(nextFullUrl)
      nextPage = nextFullUrl
      nextSubDomain =
        nextUrlObj.hostname.split('.').length > 2
          ? nextUrlObj.hostname.split('.')[0]
          : null
    }

    const dataLayer = {
      current_page: currentUrl,
      current_page_id: pageProps?.page?.id || null,
      next_page: nextPage,
      next_page_id: null,
      current_sub_domain: currentSubDomain,
      next_sub_domain: nextSubDomain,
    }

    if (pageProps?.page) {
      GTMEvent('site-view', dataLayer, pageProps)
      Akiva({ app: pageProps, type: AkivaType.View })
      Akiva({ app: pageProps, type: AkivaType.Performance })
    }
  }

  useEffect(() => {
    loginChecker()
    AOS.init({ duration: 300 })
    registerServiceWorker()

    let previousUrl: string | null = null

    const handleRouteStart = () => {
      NProgress.start()
      previousUrl = window.location.href
    }

    const handleRouteComplete = (nextUrl: string) => {
      NProgress.done()
      handleRouteChange(nextUrl, previousUrl)
    }

    Router.events.on('routeChangeStart', handleRouteStart)
    Router.events.on('routeChangeComplete', handleRouteComplete)

    handleRouteChange(window.location.href, null)

    return () => {
      Router.events.off('routeChangeStart', handleRouteStart)
      Router.events.off('routeChangeComplete', handleRouteComplete)
    }
  }, [])

  useEffect(() => {
    if (
      !window?.location?.pathname?.includes('auth') &&
      !window?.location?.pathname?.includes('my-profile')
    ) {
      // const host =
      //   process.env.NEXT_PUBLIC_ENV === NodeEnv.Production
      //     ? `.${window.location.host}`
      //     : ``

      Cookie.set('redirect_url', window.location.href)
    }
  }, [router])

  // Sync HTML lang/dir from cookie or router locale
  useEffect(() => {
    setLang(Cookie.get('language') || router.locale!)
  }, [router.locale])

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const queryClient = new QueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider {...oidcConfig}>
        <ReactSnackbarProvider>
          <main
            className={redHat.className}
            data-rtl={
              !!(pageProps as AppContextProps)?.languages?.find(
                (lng) => lng?.code === (pageProps as AppContextProps)?.language,
              )?.isRightDirection
            }
          >
            <Component {...pageProps} />
          </main>
        </ReactSnackbarProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default MyApp
